/** @format */

import type React from "react";
import { type FC, useEffect, useRef, useState } from "react";
import { cn } from "../../../util/classNameConstructor";
import Icon from "../../core/icon/Icon";

export interface ImageCardData {
  id?: string;
  mapId?: string;
  title?: string;
  imgURL?: string;
  mapbounds?: {
    _northEast: { lat: number; lng: number };
    _southWest: { lat: number; lng: number };
  };
}

export interface ImageCardProps {
  data?: any;
  selected?: boolean;
  title?: string;
  subTitle?: string;
  img?: string;
  onClick?: (data) => void;
  classNames?: {
    text?: string;
    /** Outer wrapper (e.g. max width for dense grids). */
    root?: string;
    /** Leading icon overlay in the image area. */
    icon?: string;
  };
  transition?: boolean;
}

const ImageCard: FC<ImageCardProps> = ({
  data,
  onClick,
  selected,
  classNames,
  transition = true,
}) => {
  const handleClick = () => onClick?.(data);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      handleClick();
    }
  };

  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
  }, []);

  return (
    <div
      className={cn(
        "relative aspect-square",
        classNames?.root,
        transition && "duration-1000 ease-expressive-out",
        ready || !transition
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-20",
      )}
    >
      <div
        type="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          "group/image-card grid grid-rows-[1fr_auto] h-full w-full",
          "cursor-pointer rounded-xl bg-white border border-border-default hover:bg-action-secondary-hovered hover:border-border-hovered active:bg-action-secondary-pressed active:border-border-pressed",
          selected &&
            "border-border-highlight-default bg-surface-highlight-subdued",
        )}
      >
        <div
          className={cn(
            "relative box-border w-full overflow-hidden rounded-t-[11px] border-b border-border-default bg-white flex justify-center items-center group-hover/image-card:border-border-hovered",
            selected && "border-border-highlight-default",
          )}
        >
          {data?.icon && (
            <Icon
              name={data.icon}
              className={cn(
                "text-text-subdued scale-150 opacity-50 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
                classNames?.icon,
              )}
            />
          )}

          {data?.img && (
            <img
              src={data.img}
              alt={data?.title}
              className="h-full w-full min-h-0 min-w-0 object-cover bg-gray-50"
            />
          )}
        </div>

        <span className={cn("body-md p-3", classNames?.text)}>
          {data?.title}
        </span>
      </div>
    </div>
  );
};

export default ImageCard;
